/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  Steps,
  Space,
  message,
} from "antd";
import Title from "antd/lib/typography/Title";
import days from "dayjs";
import useAuth from "../../../hooks/useAuth";
import eventService from "../../../services/eventService";
import { PlusCircleOutlined } from "@ant-design/icons";
import PageHeader from "../../headers/PageHeader";
import { categories } from "./components/eventCategories"; // Importamos los datos de eventos en un archivo separado
import {
  CAUSAS_CON_DETALLE,
  LocationSelector,
  EventTypeSelector,
  EventDetails,
} from "./components/EventFormFields";

import { useUser } from "../../../context/UserContext";

const ModalNuevoEvento = ({
  mood = "Eventos",
  item = null,
  fetchData,
  zonaPreSeleccionada = null,
}) => {
  const [open, setOpen] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [form] = Form.useForm();
  const { session, consultarEventos, locations } = useAuth();
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const user = useUser();

  // Riesgos de Proceso, Contravenciones y Delitos son exclusivos del equipo CISE;
  // el resto de equipos (p. ej. EMER) solo ve "Clave alpha" y "Emergencias" al
  // crear un evento nuevo. "Persona herida" y "Persona fallecida" se retiran
  // del menú para estos equipos, y el rótulo "Otros" se oculta (hideLabel)
  // porque ya no agrupa nada que valga la pena nombrar aparte.
  //
  // eventCategories.js no se toca: los eventos ya creados con esos riesgos
  // siguen editándose sin problema desde ModalEditarDatosEvento, que usa la
  // categoría "otros" completa para reconstruir la selección original.
  const otrosCategory = categories.find((category) => category.key === "otros");
  const visibleCategories =
    session?.user?.team === "CISE"
      ? categories.filter((category) => category.key !== "otros")
      : otrosCategory
      ? [
          {
            ...otrosCategory,
            hideLabel: true,
            subItems: otrosCategory.subItems.filter((subItem) =>
              ["clave-alpha", "emergencias"].includes(subItem.key)
            ),
          },
        ]
      : [];

  useEffect(() => {
    form.resetFields();
    setSelectedItems([]);
  }, [open]);

  // Efecto para pre-seleccionar la zona cuando se abre el modal
  useEffect(() => {
    if (open && zonaPreSeleccionada) {
      // Aquí podríamos establecer la zona pre-seleccionada en el formulario
      // Esto se manejará en el LocationSelector
    }
  }, [open, zonaPreSeleccionada]);

  const handleSubmit = (values) => {
    if (selectedItems.length === 0) {
      message.error("Selecciona al menos un tipo de evento antes de continuar.");
      return;
    }

    const distinctCategories = new Set(selectedItems.map((it) => it.category));
    const distinctSubItems = new Set(selectedItems.map((it) => it.subItem));
    if (distinctCategories.size > 1 || distinctSubItems.size > 1) {
      message.error(
        "Todas las causas seleccionadas deben pertenecer al mismo tipo y riesgo."
      );
      return;
    }

    const source =
      values?.source === "Otro" ? values?.sourceOtro : values?.source;

    const formattedDateTime = days(values.date).format("YYYY-MM-DDTHH:mm");
    const causes = selectedItems.map((it) =>
      CAUSAS_CON_DETALLE.includes(it.value) && values?.titleDetail
        ? `${it.value}-${values.titleDetail}`
        : it.value
    );

    const rawLocation = JSON.parse(values.Lugar);
    const location = {
      zone: rawLocation.zone,
      name: rawLocation.name,
      means: rawLocation.means,
      type: rawLocation.type,
      vehicle: rawLocation.vehicle,
      specific: rawLocation.specific,
      geolocation: rawLocation.geolocation,
    };

    const registro = {
      protocols: [...new Set(selectedItems.map((it) => it.protocol))],
      type: selectedItems[0].category,
      risk: selectedItems[0].subItem,
      causes,
      location,
      title: values.title,
      impact: "leve",
      date: formattedDateTime + ":00-05:00",
      userId: session?.user?.id || session?.user?.sub,
      source: source || "Plataforma Cise",
      team: session?.user?.team,
      intention: "",
    };

    setLoadingButton(true);
    CrearDatos(registro);
  };

  const CrearDatos = async (registro) => {
    try {
      const response = await eventService.crearEvento(
        registro,
        user?.idToken || ""
      );
      // crearProtocolo(registro, response?.item?.id || "id");

      message.success(
        `Tu evento se ha generado exitosamente con el ID: ${
          (registro, response?.item?.id || "id")
        }`,
        15
      );
      consultarEventos();
      // Actualizar la lista de eventos si se proporciona la función fetchData
      if (fetchData) {
        fetchData();
      }
      setOpen(false);
    } catch (error) {
      console.error("Error al crear evento:", error);
      message.error("No se logró hacer la solicitud.");
    }
    setLoadingButton(false);
  };

  return (
    <>
      <Button
        type="primary"
        onClick={() => setOpen(true)}
        icon={<PlusCircleOutlined />}
      >
        Nuevo Evento
      </Button>
      <Modal
        centered
        open={open}
        onCancel={() => setOpen(false)}
        width={"90%"}
        footer={false}
      >
        <PageHeader title="Crear Nuevo Evento" head="Eventos">
          <Title level={3}>Fecha: {days().format("YYYY/MM/DD HH:mm:ss")}</Title>
        </PageHeader>
        {form && (
          <Form
            layout="vertical"
            onFinish={handleSubmit}
            form={form}
            initialValues={{
              date: days(),
              title: mood === "Alerta" ? `Alarma - ${item?.alertName}` : "",
            }}
          >
            <Steps
              current={0}
              items={[
                { title: "Selecciona la ubicación" },
                { title: "Selecciona uno o más tipos de evento" },
                { title: "Añadir descripción" },
              ]}
            />
            <Space />
            <Row gutter={16}>
              <Col span={8}>
                <LocationSelector
                  form={form}
                  locations={locations}
                  selectedLocation={selectedLocation}
                  setSelectedLocation={setSelectedLocation}
                  zonaPreSeleccionada={zonaPreSeleccionada}
                />
              </Col>
              <Col span={8}>
                <EventTypeSelector
                  categories={visibleCategories}
                  selectedItems={selectedItems}
                  setSelectedItems={setSelectedItems}
                />
              </Col>
              <Col span={8}>
                <EventDetails selectedItems={selectedItems} />
              </Col>
            </Row>
            <Button
              htmlType="submit"
              style={{ marginTop: "20px" }}
              block
              type="primary"
              loading={loadingButton}
            >
              Crear Evento
            </Button>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default ModalNuevoEvento;
